import { NvButton } from '@simwood/nv-react-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon } from '../../../app/components/icons';
import { smsThreadsByNumberSelector } from '../../selectors/sms';
import { StoreState } from '../../store';
import NewSmsModal from '../NewSmsModal';
import SmsFeed from '../SmsFeed';
import SmsItem from '../SmsItem';
import Spinner from '../Spinner';
import * as styles from './styles.module.scss';

interface Props {
  compact?: boolean;
}

const SmsThreadList: React.FC<Props> = ({ compact }) => {
  const scrollAmountRef = useRef(0);
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const { threadsByNumber, numberOrdering } = useSelector(
    smsThreadsByNumberSelector,
  );
  const isFetching = useSelector((state: StoreState) => state.sms.isFetching);
  const [selectedThreadNumber, setSelectedThreadNumber] = useState<string>('');
  const [creatingSms, setCreatingSms] = useState(false);

  const selectedThread = useMemo(
    () => threadsByNumber[selectedThreadNumber],
    [threadsByNumber, selectedThreadNumber],
  );

  const hasSelectedThread = !!selectedThread;

  const smsThreads = useMemo(
    () => numberOrdering?.map((n) => threadsByNumber[n]),
    [threadsByNumber, numberOrdering],
  );

  useEffect(() => {
    if (!hasSelectedThread) {
      const { current } = scrollContainerRef;
      const onScroll = () => {
        scrollAmountRef.current = current?.scrollTop || 0;
      };
      current?.addEventListener('scroll', onScroll);
      return () => {
        current?.removeEventListener('scroll', onScroll);
      };
    }
  }, [hasSelectedThread]);

  useEffect(() => {
    if (!hasSelectedThread) {
      scrollContainerRef.current?.scrollTo({ top: scrollAmountRef.current });
    }
  }, [hasSelectedThread]);

  if (selectedThread) {
    return (
      <SmsFeed
        thread={selectedThread}
        compact={compact}
        onBack={() => {
          setSelectedThreadNumber('');
        }}
      />
    );
  }

  return (
    <>
      {/* Background refetches must not unmount the list or an open NewSmsModal */}
      {isFetching && !smsThreads?.length ? (
        <Spinner text='Loading' />
      ) : (
        <ul
          ref={scrollContainerRef}
          className={[styles.list, compact ? styles.compact : '']
            .join(' ')
            .trim()}
        >
          <li className={styles.buttonContainer}>
            <NvButton
              leftIcon={<PlusIcon />}
              onClick={() => {
                setCreatingSms(true);
              }}
            >
              New
            </NvButton>
          </li>
          {smsThreads?.map((thread) => {
            const message = {
              number: thread.number,
              created: thread.messages[0].created,
              body: thread.messages[0].body,
            };
            return (
              <li key={thread.number} className={styles.listElement}>
                <button
                  className={styles.listButton}
                  onClick={() => {
                    setSelectedThreadNumber(thread.number);
                  }}
                >
                  <SmsItem message={message} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {creatingSms && (
        <NewSmsModal
          fullScreen={false}
          onClose={() => {
            setCreatingSms(false);
          }}
        />
      )}
    </>
  );
};

export default React.memo(SmsThreadList);
